/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Badge } from '@im/ui-lib/zenith/Badge';
import { Alert } from '@im/ui-lib/zenith/Alert';
import { Switch } from '@im/ui-lib/zenith/Switch';
import Box from '@mui/material/Box';
import { FactorEnrollmentPopup } from './internal/FactorEnrollmentPopup';
import { useLocalizationContext, useTranslate } from '@im/react-localization';
import { AlertPopup } from './internal/AlertPopup';
import { useApiDelete, useApiGet, useApiPost } from '../../query/api-hooks';
import { useDecodedToken } from '../authorization.hook';
import { useFeatureFlag } from '../../feature-flag/useFeatureFlag';
import { ImThemeProviderZenith } from '@im/im-theme';
import { MuiProviders } from '@im/ui-lib/theme/MuiProviders';

type Factors = {
  enabled: boolean;
  googleAuthenticator: {
    enrolled: EnrolledFactor | undefined;
  };
  oktaVerify: {
    enrolled: EnrolledFactor | undefined;
  };
};

type EnrolledFactor = {
  createdDate: Date;
  factorID: string;
  factorType: string;
  lastUpdatedDate: Date;
  provider: string;
  status: string;
};

type EnrollmentAppUrls = {
  googleAuthenticator: string;
  oktaVerify: string;
};

type Props = {
  mfaPhase: string;
};

const EMAIL = 'Email';
const GOOGLE_AUTHENTICATOR = 'GoogleAuthenticator';
const OKTA_VERIFY = 'OktaVerify';
const GOOGLE_AUTHENTICATOR_ENROLLMENT_APP =
  'Mfa_GoogleAuthenticator_Enrollment_Bookmark_App';
const OKTA_VERIFY_ENROLLMENT_APP = 'Mfa_OktaVerify_Enrollment_Bookmark_App';
const RESELLER_WHITELIST = 'Mfa_Reseller_Whitelist';
const RESELLER_BLACKLIST = 'Mfa_Reseller_Blacklist';
const USER_SKIPLIST = 'Skip_Mfa_Special_Accounts';
const ENFORCE_DATE = 'Mfa_Enforce_Date';

export const Mfa = (props: Props) => {
  const { mfaPhase } = props;
  const [factors, setFactors] = useState<Factors>({
    enabled: false,
    googleAuthenticator: { enrolled: undefined },
    oktaVerify: { enrolled: undefined },
  });
  const [loaded, setLoaded] = useState<boolean>(false);
  const [blocked, setBlocked] = useState<boolean>(false);
  const getEnabledFactors = useApiGet<string[]>();
  const getEnrolledFactors = useApiGet<EnrolledFactor[]>();
  const apiPost = useApiPost<boolean>();
  const apiDelete = useApiDelete<boolean>();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState({ description: '', header: '' });
  const { locale } = useLocalizationContext();
  const [alert, setAlert] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<AlertSeverity>('success');
  const [alertText, setAlertText] = useState('alert text'); //
  const token = useDecodedToken();
  const translate = useTranslate();

  const { isReady: boKeysReady, value: boKeys } = useFeatureFlag([
    OKTA_VERIFY_ENROLLMENT_APP,
    GOOGLE_AUTHENTICATOR_ENROLLMENT_APP,
    RESELLER_WHITELIST,
    RESELLER_BLACKLIST,
    USER_SKIPLIST,
    ENFORCE_DATE,
  ]);
  const enrollmentAppUrls: EnrollmentAppUrls = useMemo(() => {
    return {
      googleAuthenticator: boKeys[GOOGLE_AUTHENTICATOR_ENROLLMENT_APP],
      oktaVerify: boKeys[OKTA_VERIFY_ENROLLMENT_APP],
    };
  }, [boKeys]);

  const exempt = useMemo(() => {
    const resellerId = token?.imoProfile?.originalResellerId;
    const resellerWhitelist = convertToArray(boKeys[RESELLER_WHITELIST]);
    const resellerBlacklist = convertToArray(boKeys[RESELLER_BLACKLIST]);

    const isMfaMandatory =
      !!resellerId &&
      (resellerWhitelist.includes('*') ||
        resellerWhitelist.includes(resellerId.toLowerCase().trim())) &&
      !resellerBlacklist.includes(resellerId.toLowerCase().trim());

    return !isMfaMandatory;
  }, [boKeys, token]);

  const isMfaSkipped = useMemo(() => {
    const userSkiplist = convertToArray(boKeys[USER_SKIPLIST]);
    const userName = token?.imoProfile?.originalUserName;
    return !!userName && userSkiplist.includes(userName.toLowerCase().trim());
  }, [boKeys, token]);

  const enforceDate = useMemo(() => {
    const ed = boKeys[ENFORCE_DATE];
    if (!ed) {
      return '';
    }
    const parts = ed.split('-');
    if (parts.length !== 3) {
      return '';
    }
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    if (!(date instanceof Date && !isNaN(date.getDate()))) {
      return '';
    }
    return date.toLocaleDateString(locale);
  }, [boKeys, locale]);

  const loadData = useCallback(() => {
    Promise.all([
      getEnabledFactors('/account-management/v1/users/groups/factors'),
      getEnrolledFactors('/account-management/v1/users/factors'),
    ]).then((responses) => {
      setFactors({
        enabled: responses[0].includes(EMAIL),
        googleAuthenticator: {
          enrolled: responses[1].find(
            (x) =>
              x.factorType === 'token:software:totp' && x.provider === 'GOOGLE'
          ),
        },
        oktaVerify: {
          enrolled: responses[1].find(
            (x) => x.factorType === 'push' && x.provider === 'OKTA'
          ),
        },
      });
    });
  }, [getEnabledFactors, getEnrolledFactors]);

  useEffect(() => {
    if (loaded) {
      return;
    }
    setLoaded(true);
    loadData();
  }, [loadData, loaded]);

  useEffect(() => {
    if (token?.azureAd) {
      setAlertText(translate('AccMgmt.Mfa_MfaNotSupported'));
      setAlertSeverity('warning');
      setAlert(true);
    } else if (isMfaSkipped && boKeysReady) {
      setAlertText(translate('AccMgmt.Mfa_MfaPermanentlyDisabled'));
      setAlertSeverity('warning');
      setAlert(true);
    } else if (exempt && boKeysReady) {
      setAlertText(translate('AccMgmt.Mfa_YouAreExempt'));
      setAlertSeverity('warning');
      setAlert(true);
    }
  }, [exempt, boKeysReady, isMfaSkipped, token, translate]);

  const handleToggle = (name: string) => {
    const emailToggled = name === EMAIL;
    if (emailToggled) {
      const enabled = !factors.enabled;
      setFactors({
        enabled: enabled,
        googleAuthenticator: {
          enrolled: factors.googleAuthenticator.enrolled,
        },
        oktaVerify: {
          enrolled: factors.oktaVerify.enrolled,
        },
      });
      setBlocked(true);
      apiPost('/account-management/v1/users/groups/factors', [
        ...(enabled ? [EMAIL] : []),
      ])
        .then((success) => {
          setBlocked(false);
          loadData();
        })
        .catch(() => {
          setBlocked(false);
        });
    }

    let factorId = '';

    const googleAuthenticatorToggled = name === GOOGLE_AUTHENTICATOR;
    if (googleAuthenticatorToggled) {
      if (!factors.googleAuthenticator.enrolled) {
        setUrl(`${enrollmentAppUrls.googleAuthenticator}?locale=${locale}`);
        setTitle({
          description: translate('AccMgmt.Mfa_FollowEnrollmentSteps'),
          header: translate('AccMgmt.Mfa_EnableGoogleAuthenticator'),
        });
        setOpen(true);
      } else {
        factorId = factors.googleAuthenticator.enrolled?.factorID;
      }
    }

    const oktaVerifyToggled = name === OKTA_VERIFY;
    if (oktaVerifyToggled) {
      if (!factors.oktaVerify.enrolled) {
        setUrl(`${enrollmentAppUrls.oktaVerify}?locale=${locale}`);
        setTitle({
          description: translate('AccMgmt.Mfa_FollowEnrollmentSteps'),
          header: translate('AccMgmt.Mfa_EnableOktaVerify'),
        });
        setOpen(true);
      } else {
        factorId = factors.oktaVerify.enrolled?.factorID;
      }
    }

    if (factorId) {
      apiDelete(`/account-management/v1/users/factors/${factorId}`).then(
        (success) => {
          if (success) {
            setFactors({
              enabled: factors.enabled,
              googleAuthenticator: {
                enrolled: googleAuthenticatorToggled
                  ? undefined
                  : factors.googleAuthenticator.enrolled,
              },
              oktaVerify: {
                enrolled: oktaVerifyToggled
                  ? undefined
                  : factors.oktaVerify.enrolled,
              },
            });
          }
        }
      );
    }
  };

  const handleCancel = () => {
    setOpen(false);
    loadData();
    setAlertText(translate('AccMgmt.Mfa_EnrollmentCanceled'));
    setAlertSeverity('error');
    setAlert(true);
  };

  const handleSuccess = () => {
    setOpen(false);
    loadData();
    setAlertText(translate('AccMgmt.Mfa_EnrollmentWasSuccessful'));
    setAlertSeverity('success');
    setAlert(true);
  };

  return (
    <ImThemeProviderZenith>
      <MuiProviders>
        {mfaPhase === '1' &&
          enforceDate &&
          !exempt &&
          !isMfaSkipped &&
          !token?.azureAd && (
            <Alert
              body={translate('AccMgmt.Mfa_ImportantSecurityUpdateText', {
                0: enforceDate,
              })}
              color="primary"
              header={translate('AccMgmt.Mfa_ImportantSecurityUpdate')}
              hidePrimaryButton
              hideSecondaryButton
              hideXCloseButton
              size="floating"
            />
          )}

        <div className="factors">
          <Typography variant="text-md-semibold">
            {translate('AccMgmt.Mfa_MultiFactorAuthentication')}
          </Typography>
          <Typography
            color="gray.500"
            sx={{ paddingBottom: '0.75rem' }}
            variant="text-sm-regular"
          >
            {translate('AccMgmt.Mfa_MultiFactorAuthenticationDescription')}
          </Typography>
          <Grid className="grid-container" container spacing={3}>
            <Grid item sx={{ width: 30 }}>
              <Box
                alt="email logo"
                component="img"
                src="/cep/assets/img/mfa/email.png"
              />
            </Grid>
            <Grid item xs>
              <Grid container>
                <Grid item xs={12}>
                  <Typography lineHeight={'22px'} variant="text-sm-semibold">
                    {translate('AccMgmt.Mfa_EmailVerification')}
                  </Typography>
                  {factors.enabled && (
                    <Badge
                      color="success"
                      label={translate('AccMgmt.Mfa_Active')}
                      size="sm"
                      type="pillColor"
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography color="gray.500" variant="text-sm-regular">
                    {translate('AccMgmt.Mfa_EmailFactorDescription')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item sx={{ width: 100 }}>
              <Switch
                checked={factors.enabled}
                disabled={
                  blocked ||
                  (factors.enabled && mfaPhase === '2' && !exempt) ||
                  !!token?.azureAd ||
                  (!factors.enabled && isMfaSkipped)
                }
                onChange={() => handleToggle(EMAIL)}
              />
            </Grid>
          </Grid>

          <Grid className="grid-container" container spacing={3}>
            <Grid item sx={{ width: 30 }}>
              <Box
                alt="okta verify logo"
                component="img"
                src="/cep/assets/img/mfa/okta-verify.png"
              />
            </Grid>
            <Grid item xs>
              <Grid container>
                <Grid item xs={12}>
                  <Typography lineHeight={'22px'} variant="text-sm-semibold">
                    {translate('AccMgmt.Mfa_OktaVerify')}
                  </Typography>
                  {!!factors.oktaVerify.enrolled && (
                    <Badge
                      color="success"
                      label={translate('AccMgmt.Mfa_Active')}
                      size="sm"
                      type="pillColor"
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography color="gray.500" variant="text-sm-regular">
                    {translate('AccMgmt.Mfa_OktaVerifyDescription')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item sx={{ width: 100 }}>
              <Switch
                checked={!!factors.oktaVerify.enrolled}
                disabled={blocked || !factors.enabled}
                onChange={() => handleToggle(OKTA_VERIFY)}
              />
            </Grid>
          </Grid>

          <Grid className="grid-container" container spacing={3}>
            <Grid item sx={{ width: 30 }}>
              <Box
                alt="google authenticator logo"
                component="img"
                src="/cep/assets/img/mfa/google-ms-authenticators_sm.png"
              />
            </Grid>
            <Grid item xs>
              <Grid container>
                <Grid item xs={12}>
                  <Typography lineHeight={'22px'} variant="text-sm-semibold">
                    {translate('AccMgmt.Mfa_GoogleAuthenticator')}
                  </Typography>
                  {!!factors.googleAuthenticator.enrolled && (
                    <Badge
                      color="success"
                      label={translate('AccMgmt.Mfa_Active')}
                      size="sm"
                      type="pillColor"
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography color="gray.500" variant="text-sm-regular">
                    {translate('AccMgmt.Mfa_GoogleAuthenticatorDescription')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item sx={{ width: 100 }}>
              <Switch
                checked={!!factors.googleAuthenticator.enrolled}
                disabled={blocked || !factors.enabled}
                onChange={() => handleToggle(GOOGLE_AUTHENTICATOR)}
              />
            </Grid>
          </Grid>
        </div>

        <FactorEnrollmentPopup
          description={title.description}
          enrollmentAppUrl={url}
          handleCancel={handleCancel}
          handleSuccess={handleSuccess}
          header={title.header}
          open={open}
        />
        <AlertPopup
          alert={alert}
          setAlert={setAlert}
          severity={alertSeverity}
          text={alertText}
        />
      </MuiProviders>
    </ImThemeProviderZenith>
  );
};

const convertToArray = (str: string) => {
  if (!str) {
    return [];
  }
  return str
    .split(/[,;]+/)
    .map((x) => x.toLowerCase().trim())
    .filter((x) => x);
};

type AlertSeverity = 'error' | 'warning' | 'success';
